import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { groupBy, map, sortBy } from 'ramda'

import { Address } from '@ecm/data/Address'
import { Area } from '@ecm/data/Area'
import { createState, State } from '@ecm/data/State'
import { runEff } from '@ecm/effect'
import StateList from '@ecm/ui/component/addresses/StateList'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'
import DefaultLayout from '@ecm/ui/layout/Default'
import { SelectedAddressesProvider } from '@ecm/ui/provider/SelectedAddressesProvider'
import { VisibleAddressesProvider } from '@ecm/ui/provider/VisibleAddressesProvider'

const DynamicStickyBanner = dynamic(() => import('@ecm/ui/component/StickyBanner'))

const DynamicMapReferenceProvider = dynamic<Record<string, unknown>>(
  () => import('@ecm/ui/provider/MapReferenceProvider').then(mod => mod.MapReferenceProvider),
  {
    ssr: false,
  }
)

const DynamicAddressesMap = dynamic(() => import('@ecm/ui/component/addresses/AddressMap'), {
  loading: function loader() {
    return <Box height="600px" />
  },
})

type AddressPageProps = {
  addresses: Address[]
  className?: string
  states: State[]
}

export const AddressesPage = ({ states, addresses }: AddressPageProps) => {
  const isSmallScreen = useSmallScreen()

  return (
    <DefaultLayout hideFinalCta seoPage="addresses">
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
        }}
      >
        <MainContainer sx={{ paddingY: '4em' }}>
          <Typography variant="h2" component="h1">
            Find your next virtual address
          </Typography>
          <Box margin="0 0 4em" maxWidth="600px">
            <Typography variant="p3">
              We have a network of real addresses available.
              <b> Use a virtual address as your own and receive your mail digitally.</b>
            </Typography>
          </Box>

          <SelectedAddressesProvider>
            <VisibleAddressesProvider initialAddresses={addresses}>
              {isSmallScreen ? (
                <StateList addresses={addresses} states={states} />
              ) : (
                <Box minHeight="600px">
                  <DynamicMapReferenceProvider>
                    <DynamicAddressesMap addresses={addresses} />
                    <StateList addresses={addresses} states={states} isMapVisible />
                  </DynamicMapReferenceProvider>
                </Box>
              )}
            </VisibleAddressesProvider>
          </SelectedAddressesProvider>
        </MainContainer>
        <DynamicStickyBanner trackingId="stickySignupBannerAddress" eventName="addressOfferClick" />
      </Box>
    </DefaultLayout>
  )
}

export default AddressesPage

export const getStaticProps: GetStaticProps = async () => {
  const addresses: Address[] = await runEff(eff => eff.getAddresses())

  const areas = await runEff(async eff => {
    try {
      const unsortedAreas: Area[] = await eff.getFeaturedAreas()
      return sortBy(a => a.name, unsortedAreas)
    } catch (e) {
      return []
    }
  })

  const groupByStates = groupBy(function (area: Area) {
    return area.stateAbbreviation
  })

  const unsortedStates = map(
    (key: string) =>
      createState(
        key,
        areas.filter(a => a.stateAbbreviation === key)
      ),
    Object.keys(groupByStates(areas))
  )

  const states = sortBy(s => s.name, unsortedStates)

  return { props: { addresses, states } }
}
